import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { psychologistInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const legalLinks = [
    { label: 'Политика конфиденциальности', href: '/privacy-policy' },
    { label: 'Условия использования сайта', href: '/terms-and-conditions' }
  ];

  const services = [
    { label: 'Индивидуальная терапия', value: 'individual' },
    { label: 'Семейная терапия', value: 'family' },
    { label: 'Детская психология', value: 'child' },
    { label: 'Кризисная помощь', value: 'crisis' },
    { label: 'Суицидальный кризис', value: 'suicide' },
    { label: 'Корпоративная психология', value: 'corporate' }
  ];

  return (
    <footer className="bg-sand-900 text-sand-200">
      {/* Main Footer */}
      <div className="container mx-auto px-6 py-12 md:py-16">
        <div className="grid gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-3">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white font-serif text-xl">
                ИЛ
              </div>
              <div>
                <p className="font-serif text-lg text-white">Ирина Лазарева</p>
                <p className="text-xs text-sand-400 tracking-[0.3em] uppercase">Клинический психолог</p>
              </div>
            </div>
            <p className="text-sand-300 text-sm leading-relaxed mb-6">
              Профессиональная психологическая помощь для взрослых, детей и семей.
            </p>
            <div className="flex gap-3">
              <a
                href={psychologistInfo.social.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-sand-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors"
                aria-label="Telegram"
              >
                <Send size={18} />
              </a>
              <a
                href={psychologistInfo.social.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-sand-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={18} />
              </a>
              <a
                href={psychologistInfo.social.vk}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-sand-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
              >
                <span className="text-xs font-bold">VK</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6">Услуги</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.value}>
                  <Link
                    to={`/services?service=${encodeURIComponent(service.value)}`}
                    className="text-sand-300 text-sm inline-flex hover:text-white transition-colors"
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & CTA */}
          <div>
            <h4 className="text-white font-semibold mb-6">Контакты</h4>
            <ul className="space-y-4 mb-6">
              <li>
                <a
                  href={`tel:${psychologistInfo.phone}`}
                  className="flex items-center gap-3 text-sand-300 hover:text-white transition-colors text-sm"
                >
                  <Phone size={16} className="text-teal-300" />
                  {psychologistInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${psychologistInfo.email}`}
                  className="flex items-center gap-3 text-sand-300 hover:text-white transition-colors text-sm"
                >
                  <Mail size={16} className="text-teal-300" />
                  {psychologistInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-sand-300 text-sm">
                <MapPin size={16} className="text-teal-300 flex-shrink-0 mt-0.5" />
                {psychologistInfo.address}
              </li>
              <li className="flex items-start gap-3 text-sand-300 text-sm">
                <Clock size={16} className="text-teal-300 flex-shrink-0 mt-0.5" />
                <span>Ежедневно: {psychologistInfo.workingHours.weekdays}</span>
              </li>
            </ul>
            <Link to="/appointment">
              <Button variant="outline" className="border-sand-700 text-sand-200 hover:bg-sand-800 hover:text-white">
                Связаться
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-sand-800">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-2 text-sand-400 text-sm">
              <p>© {currentYear} Ирина Лазарева. Все права защищены.</p>
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-1 text-sand-400 text-sm">
              <span>Сделано с</span>
              <Heart size={14} className="text-rose-500 fill-rose-500" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
